import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  CalendarIcon,
  CreditCard,
  DollarSign,
  Truck,
} from "lucide-react";
import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";

type PaymentMode = "CAPTURE" | "COD";
type PaymentMethod = "CASH" | "BANK";
type BankCode = "ABA" | "WING" | "ACLEDA";

export type PaymentDialogValues = {
  mode: PaymentMode;
  method: PaymentMethod;
  bank?: BankCode;
  amount?: number;
  referenceNo?: string;
  paidAt: string;
  note?: string;
};

type PaymentDialogProps = {
  orderId: string;
  totalAmount: number;
  paidAmount: number;
  open: boolean;
  isCOD?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: PaymentDialogValues) => void;
};

const BANKS: { value: BankCode; label: string }[] = [
  { value: "ABA", label: "ABA Bank" },
  { value: "WING", label: "Wing" },
  { value: "ACLEDA", label: "ACLEDA" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatCurrency = (value: number) => value.toFixed(2);

function getValidationError({
  mode,
  method,
  bank,
  amount,
  remaining,
}: {
  mode: PaymentMode;
  method: PaymentMethod;
  bank?: BankCode;
  amount: number;
  remaining: number;
}): string | null {
  if (mode !== "CAPTURE") return null;

  if (!amount || amount <= 0) {
    return "Amount must be greater than 0.";
  }
  if (amount > remaining) {
    return "Amount cannot be greater than remaining.";
  }
  if (method === "BANK" && !bank) {
    return "Please select a bank.";
  }
  return null;
}

export function PaymentDialog({
  orderId,
  totalAmount,
  paidAmount,
  open,
  onOpenChange,
  isCOD,
  onSubmit,
}: PaymentDialogProps) {
  const remaining = Math.round(Math.max(totalAmount - paidAmount, 0) * 100) / 100;

  const [mode, setMode] = useState<PaymentMode>("CAPTURE");
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [bank, setBank] = useState<BankCode | undefined>();
  const [amount, setAmount] = useState<number>(remaining);
  const [referenceNo, setReferenceNo] = useState("");
  const [paidAt, setPaidAt] = useState<string>(todayISO());
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remainingRef = useRef(remaining);
  remainingRef.current = remaining;

  useEffect(() => {
    if (!open) return;

    setMode(isCOD ? "COD" : "CAPTURE");
    setMethod("CASH");
    setBank(undefined);
    setAmount(remainingRef.current);
    setReferenceNo("");
    setPaidAt(todayISO());
    setNote("");
    setError(null);
  }, [open, isCOD]);

  const validationError = getValidationError({
    mode,
    method,
    bank,
    amount,
    remaining,
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const payload: PaymentDialogValues = {
      mode,
      method: mode === "COD" ? "CASH" : method,
      bank: mode === "CAPTURE" ? bank : undefined,
      amount: mode === "CAPTURE" ? amount : undefined,
      referenceNo:
        mode === "CAPTURE" && referenceNo.trim()
          ? referenceNo.trim()
          : undefined,
      paidAt,
      note: note.trim() || undefined,
    };

    onSubmit(payload);
    setIsSubmitting(false);
    onOpenChange(false);
  };

  const handleAmountChange = (value: string) => {
    const parsed = Number(value);
    setAmount(Number.isFinite(parsed) ? Math.max(parsed, 0) : 0);
  };

  const disableSubmit = isSubmitting || validationError !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle>Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-4 px-6">

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 text-xs sm:text-sm">
              <SummaryBox label="Order Total" value={totalAmount} />
              <SummaryBox label="Paid" value={paidAmount} muted />
              <SummaryBox label="Remaining" value={remaining} highlight />
            </div>

            {/* Mode: Capture vs COD */}
            <div className="space-y-2">
              <Label className="text-sm">Payment Mode</Label>
              <RadioGroup
                className="grid grid-cols-2 gap-3"
                value={mode}
                onValueChange={(val) => {
                  setMode(val as PaymentMode);
                  setError(null);
                }}
              >
                <RadioCard
                  value="CAPTURE"
                  selected={mode === "CAPTURE"}
                  activeClassName="border-blue-500 bg-blue-50 text-blue-700"
                  icon={<DollarSign className="h-4 w-4" />}
                >
                  Capture Payment
                </RadioCard>

                <RadioCard
                  value="COD"
                  selected={mode === "COD"}
                  activeClassName="border-amber-500 bg-amber-50 text-amber-800"
                  icon={<Truck className="h-4 w-4" />}
                >
                  Mark as COD
                </RadioCard>
              </RadioGroup>
            </div>

            {/* Method only for Capture */}
            {mode === "CAPTURE" && (
              <div className="space-y-2">
                <Label className="text-sm">Payment Method</Label>
                <RadioGroup
                  className="flex gap-3"
                  value={method}
                  onValueChange={(val) => {
                    setMethod(val as PaymentMethod);
                    setError(null);
                  }}
                >
                  <RadioCard
                    value="CASH"
                    selected={method === "CASH"}
                    activeClassName="border-blue-500 bg-blue-50 text-blue-700"
                    icon={<DollarSign className="h-4 w-4" />}
                    className="flex-1"
                  >
                    Cash
                  </RadioCard>

                  <RadioCard
                    value="BANK"
                    selected={method === "BANK"}
                    activeClassName="border-blue-500 bg-blue-50 text-blue-700"
                    icon={<CreditCard className="h-4 w-4" />}
                    className="flex-1"
                  >
                    Bank Transfer
                  </RadioCard>
                </RadioGroup>
              </div>
            )}

            {/* Bank details */}
            {mode === "CAPTURE" && method === "BANK" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Bank</Label>
                  <Select
                    value={bank}
                    onValueChange={(val) => {
                      setBank(val as BankCode);
                      setError(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent className="z-[1502]">
                      {BANKS.map((b) => (
                        <SelectItem key={b.value} value={b.value}>
                          {b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Reference / Slip No.</Label>
                  <Input
                    placeholder="e.g. 0123456789"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Amount (only capture) + date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {mode === "CAPTURE" && (
                <div className="space-y-1.5">
                  <Label>Amount to capture</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={amount.toString()}
                      onChange={(e) => {
                        handleAmountChange(e.target.value);
                        setError(null);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      className="text-[11px]"
                      onClick={() => {
                        setAmount(remaining);
                        setError(null);
                      }}
                      disabled={remaining <= 0}
                    >
                      Full
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Remaining: {formatCurrency(remaining)}
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>
                  {mode === "CAPTURE" ? "Paid Date" : "Expected Pay Date"}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={paidAt}
                    onChange={(e) => setPaidAt(e.target.value)}
                  />
                  <CalendarIcon className="h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                placeholder={
                  mode === "CAPTURE"
                    ? "Any note about this payment..."
                    : "Note about COD (e.g. pay at delivery, collect at school)..."
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {/* Error message */}
            {error && (
              <p className="text-[12px] text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {error}
              </p>
            )}
          </div>
          <DialogFooter className="pt-4 pb-6 gap-2 px-6 shrink-0 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={disableSubmit}>
              {mode === "CAPTURE"
                ? isSubmitting
                  ? "Saving..."
                  : "Confirm Payment"
                : isSubmitting
                  ? "Saving..."
                  : "Mark as COD"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type SummaryBoxProps = {
  label: string;
  value: number;
  muted?: boolean;
  highlight?: boolean;
};

function SummaryBox({ label, value, muted, highlight }: SummaryBoxProps) {
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 flex flex-col gap-0.5",
        muted && "bg-slate-50 text-slate-500",
        highlight && "bg-emerald-50 border-emerald-300 text-emerald-800"
      )}
    >
      <span className="text-[11px] uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm font-semibold">
        {formatCurrency(value)}
      </span>
    </div>
  );
}

type RadioCardProps = {
  value: string;
  selected: boolean;
  activeClassName: string;
  icon: ReactNode;
  children: ReactNode;
  className?: string;
};

function RadioCard({
  value,
  selected,
  activeClassName,
  icon,
  children,
  className,
}: RadioCardProps) {
  return (
    <label
      className={cn(
        "flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer",
        selected && activeClassName,
        className
      )}
    >
      <RadioGroupItem value={value} className="sr-only" />
      {icon}
      <span>{children}</span>
    </label>
  );
}
